import * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

// Type-safe variant and size constants
export const BUTTON_VARIANTS = ['default', 'secondary', 'tertiary', 'ghost', 'outline', 'link', 'destructive'] as const;
export const BUTTON_SIZES = ['xs', 'default', 'sm', 'lg', 'icon', 'icon-sm'] as const;

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];

// see https://www.figma.com/design/T57BS1heDUWGJZYLhn0DLv/Design-System?node-id=1687-73192&t=cmIhoe8pNEyplbaz-4
const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    {
        variants: {
            variant: {
                // Primary: bin-blue-600 bg, white text
                default:
                    'bg-primary-default text-primary-on-default-text shadow-xs hover:bg-primary-hover active:bg-primary-active focus-visible:ring-border-focus disabled:bg-primary-disabled disabled:text-text-disabled',
                // Secondary: neutral-200 bg, secondary text
                secondary:
                    'bg-secondary-default text-text-secondary shadow-xs hover:bg-secondary-hover active:bg-secondary-active focus-visible:ring-border-focus disabled:bg-border-default disabled:text-text-disabled',
                // Tertiary: white bg, neutral-400 border, secondary text
                tertiary:
                    'border border-border-strong bg-surface-tertiary text-text-secondary hover:bg-surface-secondary hover:text-text-primary active:bg-surface-primary active:text-text-primary focus-visible:ring-border-focus disabled:bg-transparent disabled:border-border-default disabled:text-text-disabled',
                // Ghost: intentionally left as shadcn default to avoid breaking changes
                ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
                // Outline: bin-blue-500 border, primary text
                outline:
                    'border border-primary-border bg-transparent text-primary-on-surface-text shadow-xs hover:bg-primary-surface-default active:bg-primary-surface-hover focus-visible:ring-border-focus disabled:bg-transparent disabled:border-border-default disabled:text-text-disabled',
                // Link: text only with underline
                link: 'text-primary-on-surface-text underline-offset-4 hover:underline focus-visible:ring-border-focus disabled:text-text-disabled disabled:no-underline',
                // Destructive: industrial-red-500 bg, white text
                destructive:
                    'bg-destructive-default text-destructive-on-default-text shadow-xs hover:bg-destructive-hover active:bg-destructive-default focus-visible:ring-border-focus disabled:bg-primary-disabled disabled:text-text-disabled',
            },
            size: {
                // Matches the xs step already used by Toolbar, ActionFooter and DatePicker.
                xs: 'h-6 px-2 py-1 text-xs gap-1 has-[>svg]:px-1.5',
                default: 'h-10 px-4 py-2 text-sm has-[>svg]:px-3',
                sm: 'h-8 px-3 py-1.5 text-xs gap-1.5 has-[>svg]:px-2.5',
                lg: 'h-11 px-6 py-2.5 text-base has-[>svg]:px-4',
                icon: 'size-10',
                'icon-sm': 'size-8',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

const Button = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> &
        VariantProps<typeof buttonVariants> & {
            asChild?: boolean;
        }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} ref={ref} />;
});

Button.displayName = 'Button';

export { Button, buttonVariants };
